'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createBrowserClient } from '@/lib/supabase-clients';

export interface ProcessingStatus {
  id: string;
  sessionId: string;
  postId: string;
  type: 'video_edit' | 'transcript_edit' | 'export';
  stage: 'queued' | 'downloading' | 'processing' | 'uploading' | 'transcribing' | 'applying_edits' | 'complete' | 'failed';
  progress: number;
  message: string;
  error?: string;
  startedAt: string;
  completedAt?: string;
  estimatedCompletion?: string;
}

interface ProcessingStatusContextValue {
  statuses: ProcessingStatus[];
  activeCount: number;
  addStatus: (status: ProcessingStatus) => void;
  updateStatus: (sessionId: string, updates: Partial<ProcessingStatus>) => void;
  removeStatus: (sessionId: string) => void;
  getStatus: (sessionId: string) => ProcessingStatus | undefined;
  clearCompleted: () => void;
  isLoading: boolean;
}

const ProcessingStatusContext = createContext<ProcessingStatusContextValue | undefined>(undefined);

const STORAGE_KEY = 'processing-statuses';
const POLL_INTERVAL = 10000; // 10 seconds - reduced from 2 seconds to avoid excessive API calls
const COMPLETED_RETENTION = 24 * 60 * 60 * 1000; // 24 hours

export function ProcessingStatusProvider({ children }: { children: React.ReactNode }) {
  const [statuses, setStatuses] = useState<ProcessingStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const supabase = createBrowserClient();

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Filter out expired/stale statuses
        const now = Date.now();
        const filtered = parsed.filter((s: ProcessingStatus) => {
          // Remove all completed statuses - they show in Recent Exports section now
          if (s.stage === 'complete') {
            console.log(`[ProcessingStatus] Removing completed status from localStorage: ${s.postId}`);
            return false;
          }

          // Remove old failed statuses (older than 1 hour)
          if (s.stage === 'failed') {
            const startedAt = new Date(s.startedAt).getTime();
            const ageHours = (now - startedAt) / (60 * 60 * 1000);
            if (ageHours > 1) {
              console.log(`[ProcessingStatus] Removing old failed status from localStorage: ${s.postId}`);
              return false;
            }
          }

          // Remove stuck processing statuses (older than 10 minutes)
          if (s.stage !== 'failed') {
            const startedAt = new Date(s.startedAt).getTime();
            const ageMinutes = (now - startedAt) / (60 * 1000);
            if (ageMinutes > 10) {
              console.log(`[ProcessingStatus] Removing stuck status from localStorage: ${s.postId} (${ageMinutes.toFixed(1)} minutes old)`);
              return false;
            }
          }

          return true;
        });
        setStatuses(filtered);
      } catch (error) {
        console.error('Failed to load processing statuses from storage:', error);
        // Clear corrupted localStorage
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  // Save to localStorage whenever statuses change
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(statuses));
    }
  }, [statuses, isLoading]);

  // Don't auto-cleanup on mount - let user trigger it manually if needed
  // This was causing unnecessary API calls on every page load

  // Poll for status updates from database
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        // Get user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get user profile
        const { data: profile } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();

        if (!profile) return;

        // Fetch active and recently completed processing statuses
        // Include completed/failed statuses from the last 5 minutes to ensure UI updates
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();

        console.log('[ProcessingStatus] Fetching statuses for user:', profile.id);

        // Simplified query - fetch ALL recent statuses to debug
        const { data: dbStatuses, error: fetchError } = await supabase
          .from('processing_status')
          .select('*')
          .eq('user_id', profile.id)
          .gte('created_at', fiveMinutesAgo)
          .order('created_at', { ascending: false });

        if (fetchError) {
          console.error('[ProcessingStatus] Error fetching statuses:', fetchError);
        }

        console.log('[ProcessingStatus] Fetched statuses from DB:', {
          count: dbStatuses?.length || 0,
          statuses: dbStatuses?.map(s => ({
            postId: s.post_id,
            stage: s.stage,
            progress: s.progress,
            sessionId: s.session_id
          }))
        });

        if (dbStatuses) {
          setStatuses(prev => {
            // Group by postId to keep only the latest status per post
            const statusByPost = new Map<string, ProcessingStatus>();

            // First, add existing statuses from state
            prev.forEach(s => {
              if (s.stage === 'complete' || s.stage === 'failed') {
                // Keep completed/failed statuses for a while
                statusByPost.set(`${s.postId}-${s.sessionId}`, s);
              } else {
                // For active statuses, use postId as key to prevent duplicates
                statusByPost.set(s.postId, s);
              }
            });

            // Update with database statuses (overwrites old ones for same post)
            dbStatuses.forEach(dbStatus => {
              // Skip stale statuses (older than 10 minutes for active statuses)
              const startedAt = new Date(dbStatus.started_at || dbStatus.created_at).getTime();
              const now = Date.now();
              const ageMinutes = (now - startedAt) / (60 * 1000);

              // Log completed statuses for debugging
              if (dbStatus.stage === 'complete') {
                console.log(`[ProcessingStatus] Found completed status:`, {
                  postId: dbStatus.post_id,
                  sessionId: dbStatus.session_id,
                  stage: dbStatus.stage,
                  completedAt: dbStatus.completed_at
                });
              }

              // If status is stuck in processing for more than 10 minutes, mark as failed
              if (ageMinutes > 10 && dbStatus.stage !== 'complete' && dbStatus.stage !== 'failed') {
                console.log(`[ProcessingStatus] Auto-failing stale status for post ${dbStatus.post_id} (${ageMinutes.toFixed(1)} minutes old)`);
                // Auto-cleanup by marking as failed
                const status: ProcessingStatus = {
                  id: dbStatus.id,
                  sessionId: dbStatus.session_id,
                  postId: dbStatus.post_id,
                  type: dbStatus.type as ProcessingStatus['type'],
                  stage: 'failed',
                  progress: dbStatus.progress,
                  message: 'Processing timed out after 10 minutes',
                  error: 'Task exceeded maximum processing time',
                  startedAt: dbStatus.started_at || dbStatus.created_at,
                  completedAt: new Date().toISOString(),
                  estimatedCompletion: null
                };
                statusByPost.set(`${dbStatus.post_id}-timeout`, status);

                // Don't trigger cleanup API on every stale status - it's too many requests
                // The manual cleanup button is sufficient

                return;
              }

              const status: ProcessingStatus = {
                id: dbStatus.id,
                sessionId: dbStatus.session_id,
                postId: dbStatus.post_id,
                type: dbStatus.type as ProcessingStatus['type'],
                stage: dbStatus.stage as ProcessingStatus['stage'],
                progress: dbStatus.progress,
                message: dbStatus.message,
                error: dbStatus.error,
                startedAt: dbStatus.started_at || dbStatus.created_at,
                completedAt: dbStatus.completed_at,
                estimatedCompletion: dbStatus.estimated_completion
              };

              // Use consistent key for all statuses
              // For completed/failed, use composite key to allow multiple completed sessions per post
              // For active statuses, use just postId to prevent duplicates
              const mapKey = (dbStatus.stage === 'complete' || dbStatus.stage === 'failed')
                ? `${dbStatus.post_id}-${dbStatus.session_id}`
                : dbStatus.post_id;

              statusByPost.set(mapKey, status);
            });

            // Convert map back to array and filter out old completed statuses
            const now = Date.now();
            const finalStatuses = Array.from(statusByPost.values()).filter(s => {
              if (s.stage === 'complete' && s.completedAt) {
                const completedTime = new Date(s.completedAt).getTime();
                // Keep completed for 5 minutes only
                return now - completedTime < 5 * 60 * 1000;
              }
              return true;
            });

            console.log('[ProcessingStatus] Final statuses after merge:', {
              count: finalStatuses.length,
              statuses: finalStatuses.map(s => ({
                postId: s.postId,
                sessionId: s.sessionId,
                stage: s.stage,
                progress: s.progress
              }))
            });

            return finalStatuses;
          });
        }

        // Skip Redis check for now - it's causing too many API calls and authentication issues
        // The database polling is sufficient for status updates
      } catch (error) {
        console.error('Failed to fetch processing statuses:', error);
      }
    };

    // Initial fetch
    fetchStatuses();

    // Set up polling
    const interval = setInterval(fetchStatuses, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [supabase]);

  const addStatus = useCallback((status: ProcessingStatus) => {
    setStatuses(prev => {
      // Remove any existing status for the same post before adding new one
      const filtered = prev.filter(s => s.postId !== status.postId);
      return [...filtered, status];
    });
  }, []);

  const updateStatus = useCallback((sessionId: string, updates: Partial<ProcessingStatus>) => {
    setStatuses(prev => prev.map(s =>
      s.sessionId === sessionId ? { ...s, ...updates } : s
    ));
  }, []);

  const removeStatus = useCallback((sessionId: string) => {
    setStatuses(prev => prev.filter(s => s.sessionId !== sessionId));
  }, []);

  const getStatus = useCallback((sessionId: string) => {
    return statuses.find(s => s.sessionId === sessionId);
  }, [statuses]);

  const clearCompleted = useCallback(() => {
    // Clear all completed, failed, and stuck statuses
    setStatuses(prev => {
      const now = Date.now();
      return prev.filter(s => {
        // Keep only active, non-stuck statuses
        if (s.stage === 'complete' || s.stage === 'failed') {
          return false;
        }

        // Remove stuck statuses (older than 10 minutes)
        const startedAt = new Date(s.startedAt).getTime();
        const ageMinutes = (now - startedAt) / (60 * 1000);
        if (ageMinutes > 10) {
          console.log(`[ProcessingStatus] Clearing stuck status: ${s.postId}`);
          return false;
        }

        return true;
      });
    });

    // Also clear localStorage to ensure clean state
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const activeCount = statuses.filter(s =>
    s.stage !== 'complete' && s.stage !== 'failed'
  ).length;

  return (
    <ProcessingStatusContext.Provider
      value={{
        statuses,
        activeCount,
        addStatus,
        updateStatus,
        removeStatus,
        getStatus,
        clearCompleted,
        isLoading
      }}
    >
      {children}
    </ProcessingStatusContext.Provider>
  );
}

export function useProcessingStatus() {
  const context = useContext(ProcessingStatusContext);
  if (context === undefined) {
    throw new Error('useProcessingStatus must be used within a ProcessingStatusProvider');
  }
  return context;
}

// Export the ProcessingStatus type for use in other components
export type { ProcessingStatus };